import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import styles from './AktueltSection.module.scss';
import { PrimaryButton } from '../../../components';
import { getFrontPageUi } from '../../../lib/front-page-ui';
import { getLanguageFromPath } from '../../../lib/polylang';

// Helper function to truncate text
const truncateWords = (text, limit = 20) => {
  if (!text) return '';
  const strippedText = text.replace(/<[^>]*>/g, '');
  const words = strippedText.trim().split(/\s+/);
  if (words.length <= limit) return strippedText;
  return words.slice(0, limit).join(' ') + '...';
};

const formatDate = (dateString, locale = 'no-NO') => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

export default function AktueltSection({ posts = [], ui }) {
  const router = useRouter();
  const lang = getLanguageFromPath(router?.asPath || '/').code;
  if (!posts?.length) return null;

  const copy = ui || getFrontPageUi(lang).aktuelt;
  const dateLocale = copy.dateLocale || (lang === 'en' ? 'en-GB' : 'no-NO');
  const articlesBase = lang === 'en' ? '/en/articles' : '/artikler';

  return (
    <section className={styles.aktueltSection}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <span className={styles.label}>{copy.label}</span>
            <h2 className={styles.title}>{copy.title}</h2>
            <p className={styles.description}>{copy.description}</p>
          </div>
          <div className={styles.headerCta}>
            <PrimaryButton 
              href={copy.articlesHref} 
              text={copy.seeAllArticles}
              light={false}
            />
          </div>
        </div>

        {/* Articles Grid */}
        <div className={styles.articlesGrid}>
          {posts.map((post) => (
            <Link 
              key={post.id} 
              href={`${articlesBase}/${post.slug}`}
              className={styles.articleCard}
            >
              {post.featuredImage?.node && (
                <div className={styles.imageWrapper}>
                  <Image
                    src={post.featuredImage.node.mediaItemUrl}
                    alt={post.featuredImage.node.altText || post.title}
                    fill
                    sizes="(max-width: 600px) 120px, (max-width: 900px) 50vw, (max-width: 1200px) 33vw, 280px"
                    style={{ objectFit: 'cover' }}
                    quality={65}
                  />
                </div>
              )}
              <div className={styles.cardContent}>
                <div className={styles.meta}>
                  {post.categories?.nodes?.[0]?.name && (
                    <span className={styles.category}>
                      {post.categories.nodes[0].name}
                    </span>
                  )}
                  <span className={styles.date}>{formatDate(post.date, dateLocale)}</span>
                </div>
                <h3 className={styles.articleTitle}>{post.title}</h3>
                <p 
                  className={styles.excerpt}
                  dangerouslySetInnerHTML={{ __html: truncateWords(post.excerpt, 15) }}
                />
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile CTA */}
        <div className={styles.mobileCta}>
          <PrimaryButton 
            href={copy.articlesHref} 
            text={copy.seeAllArticles}
            light={false}
          />
        </div>
      </div>
    </section>
  );
}
